/**
 * SSE -> FTUE signal bridge (PEL-515).
 *
 * Maps the existing job-matching SSE events (no new transport) onto the engine's
 * named signals. Mount once near `useEventStream` in the app layout.
 *
 *   job_matching_status_changed { status: 'pending' | 'matching' } -> 'scanStarted'
 *   job_matching_progress { completed >= 1 } -> 'firstCardScored'
 *   job_matching_status_changed { status: 'completed' } -> 'scanComplete'
 *
 * PEL-613 (Part 2): the Screen-4 tour is reused on the job-search results view,
 * which is driven by the parallel `job_search_*` events (a custom search agent,
 * not a resume scan). Map those onto the SAME signals so the orientation step's
 * event-gating works there identically:
 *
 *   job_search_progress { completed >= 1 } -> 'firstCardScored'
 *   job_search_status_changed { status: 'completed' } -> 'scanComplete'
 *
 * `scanStarted` (PEL-519) gates the Screen 3 handoff step so its timeout floor
 * arms only while a scan is genuinely running — never on a resume that was
 * analyzed but never matched. Status values mirror `isActiveMatchingStatus`
 * (`pending`/`matching`) in use-documents.ts; `JobSearchStatus` uses the same
 * terminal `completed` value.
 *
 * See use-event-stream.ts and the PEL-515 code-grounded findings comment.
 */

import { useSSEEvent } from '@/hooks/use-event-stream'
import { useFtueStore } from './ftue.store'

/** Loose view of the progress payload — `completed`/`total` are emitted by the
 * backend but not yet in the typed SSE data shape. */
interface JobMatchingProgressData {
  completed?: number
  total?: number
  status?: string
}

export function useFtueSseBridge(): void {
  const signalEvent = useFtueStore((s) => s.signalEvent)

  useSSEEvent(
    [
      'job_matching_progress',
      'job_matching_status_changed',
      'job_search_progress',
      'job_search_status_changed',
    ],
    (event) => {
      const data = event.data as JobMatchingProgressData | undefined

      // First scored card on either surface -> firstCardScored.
      if (event.type === 'job_matching_progress' || event.type === 'job_search_progress') {
        if ((data?.completed ?? 0) >= 1) {
          signalEvent('firstCardScored')
        }
        return
      }

      // job_matching_status_changed carries the scan lifecycle (incl. the
      // `scanStarted` arming for PEL-519's handoff timeout). job_search_status_changed
      // only needs the terminal `completed` -> scanComplete for the reused tour.
      if (event.type === 'job_matching_status_changed') {
        if (data?.status === 'pending' || data?.status === 'matching') {
          signalEvent('scanStarted')
        } else if (data?.status === 'completed') {
          // Terminal success value is `completed` (JobMatchingStatus in use-documents.ts /
          // services enums.py), NOT `done`. The earlier `'done'` was dead code, so
          // `scanComplete` never fired — gating PEL-520's Phase 2 on a signal that
          // never arrived.
          signalEvent('scanComplete')
        }
        return
      }

      if (event.type === 'job_search_status_changed') {
        if (data?.status === 'completed') {
          signalEvent('scanComplete')
        }
      }
    },
  )
}
